import 'element-internals-polyfill';
import {
  AttachInternals,
  Component,
  Element,
  Event,
  EventEmitter,
  Host,
  Listen,
  Method,
  Prop,
  State,
  Watch,
  h,
} from '@stencil/core';
import { v4 as uuidv4 } from 'uuid';
import { awaitDefinedResult, getOverriddenValidationMessage, selectFormSubmitters } from '../../utils/utils';
import { libraryComponentOptions } from '../../settings/settings';

/** A Radio button with space for a label and error message for use in forms.
 * Fires the same events as input[type=radio]: input, change, blur, focus. Input and change fire at the same time.
 */
@Component({
  tag: 'omk-radio',
  styleUrl: 'omk-radio.scss',
  // We can't use the shadow DOM without throwing away the browser's keyboard controls for radios in the same group.
  shadow: false,
  scoped: true,
  formAssociated: true,
})
export class OmkRadio {
  // We can't call this property `name` because that interferes with how Material finds radios in the same group.
  /** Name for the radio button group. This is the key used in form data. */
  @Prop({ reflect: true }) groupName: string = '';
  /** Value used in form data when checked. Defaults to 'on'. */
  @Prop({ mutable: true }) value: string = 'on';
  /** ID for an external HTML form to include the control in. */
  @Prop({ reflect: true }) form: string;
  /** Sets or indicates whether this radio option is selected. Only one in the group can be checked at once. */
  @Prop({ reflect: true, mutable: true }) checked: boolean = false;
  /** Disables interaction and form inclusion for the radio option. */
  @Prop({ reflect: true }) disabled: boolean = false;
  /** A user-visible label for the radio option.
   * If `ariaLabel` is not set, this value will be used to describe the component to assistive technologies too. */
  @Prop() label: string;
  /** Places the label before the radio option when present. By default, the label comes after the radio button. */
  @Prop() labelBefore: boolean = false;
  /** User-visible sublabel text. Displays below the label with less emphasis. */
  @Prop() sublabel: string;
  /** A label describing the option for assistive technologies like screen-readers.
   * Doesn't need to be set if it should be the same as `label` */
  @Prop() ariaLabel: string | null;
  /** Marks the radio group as required, meaning a selection must be made before the associated form can be submitted.
   * Only one radio option in the group needs to be marked as required; it applies to the whole group.
   * The validation message will be shown under the one marked as required, and will only appear once the element is touched or an attempt is made to submit the form. */
  @Prop() required: boolean = false;
  /** Sets a custom validation message and marks the radio group as invalid for form purposes. When set through the JS property, the error message will be shown immediately.
   * When set through the HTML attribute, the validation message will only appear once the element is touched or an attempt is made to submit the form. */
  @Prop() customValidationMessage: string;
  /** @internal Do not set - regard as read-only. Forms check this property before submit and when checkValidity() is called. */
  @Prop({ mutable: true }) validity: ValidityState;
  /** @internal Do not set - regard as read-only. The current validation message whether custom or built-in. */
  @Prop({ mutable: true }) validationMessage: string;
  /** Show validation message immediately instead of waiting for user interaction or form submission. */
  @Prop() alwaysShowValidationMessage: boolean;
  /** Hides the inline validation messages. Used when error messages are to be displayed using other means other than the inline capability. */
  @Prop() hideValidationMessage: boolean;
  /** @internal */
  @Prop({ mutable: true }) isGroupInvalid: boolean = false;
  /** @internal Do not set - regard as read-only. Used to stop the OmkForm from trying to focus on this element as it is merely a container for actual form elements. */
  @Prop({ mutable: true }) focusable: boolean = false;

  @Element() host: HTMLElement;
  innerInputElement: HTMLInputElement;
  hasRegisteredFormCallback: boolean = false;
  /** We only use `internals` for validation. Setting the form value is taken care of by the inner Material component. Setting it ourselves would duplicate the form entry. */
  @AttachInternals() internals: ElementInternals;
  @State() touched: boolean = false;
  @State() hasFocus: boolean = false;
  @State() hasHover: boolean = false;
  /** Represents the identifier of a radio. */
  @State() radioId: string;

  private initialChecked: boolean;
  private initialValue: string;

  /** Fired to alert the rest of the radio group that the value has changed so they can uncheck themselves. */
  @Event({ eventName: 'omkRadioGroupChange', composed: true, cancelable: false, bubbles: true })
  radioGroupChange: EventEmitter<string>;

  @Listen('focus')
  @Listen('focusin')
  focusCompletedHandler() {
    this.innerInputElement.focus();
    this.hasFocus = true;
  }

  @Listen('omkRadioGroupChange', { target: 'document' })
  handleRadioGroupChange(event: CustomEvent) {
    if (this.groupName === event.detail) {
      this.touched = true;
      this.checked = this.host === event.target;
    }
  }

  inputCompletedHandler() {
    if (this.groupName) {
      this.radioGroupChange.emit(this.groupName);
    }
  }

  blurCompletedHandler() {
    this.hasFocus = false;
    this.touched = true;
  }

  async componentDidLoad() {
    if (this.alwaysShowValidationMessage) {
      this.touched = true;
    }
    await awaitDefinedResult(() => this.innerInputElement);
    if (this.form) {
      this.innerInputElement.setAttribute('form', this.form);
    }
    if (!this.hasRegisteredFormCallback) {
      this.formAssociatedCallback(this.internals.form);
    }
  }

  /**
   * Sets the unique radioId for the component when it is about to load.
   *
   * @returns {void}
   */
  componentWillLoad() {
    this.radioId = `omk-${uuidv4()}`;
    this.initialChecked = this.checked;
    this.initialValue = this.value;
  }

  @Listen('blur')
  @Listen('change')
  @Listen('input')
  async handleUserInteraction() {
    if (this.innerInputElement) {
      this.checked = this.innerInputElement.checked;
    }
    this.touched = true;
    await this.validate(true);

    const parentList = this.host.closest('omk-list') as any;
    if (parentList && typeof parentList.validate === 'function') {
      await parentList.validate();
    }
  }

  @Watch('alwaysShowValidationMessage')
  @Watch('customValidationMessage')
  showValidationChanged(newValue) {
    if (newValue) {
      this.touched = true;
    }
  }

  @Watch('form')
  updateForm(newValue: string) {
    // Material doesn't expose a form prop setter matching the attribute like it does for `name` so we have to explicitly set the attribute.
    if (newValue && this.innerInputElement) {
      this.innerInputElement.setAttribute('form', newValue);
    }
  }

  @Listen('click')
  handleClick() {
    this.innerInputElement.focus();
    this.innerInputElement.click();
    this.checked = true;
    if (this.groupName) {
      this.radioGroupChange.emit(this.groupName);
    }
  }

  @Listen('pointerenter')
  handleHover() {
    this.hasHover = true;
  }
  @Listen('pointerleave')
  handleHoverOut() {
    this.hasHover = false;
  }

  /** Reset the radio to its default value. */
  @Method()
  async reset() {
    this.value = this.initialValue;
    this.checked = this.initialChecked;
    this.touched = false;
    this.internals.setFormValue(this.value);
    this.internals.setValidity({});
    this.validate();
  }

  @Method()
  async customFocus() {
    this.innerInputElement.focus();
    this.hasFocus = true;
  }

  render() {
    return (
      <Host class={{ 'touched': this.touched, 'invalid-group': this.isGroupInvalid }}>
        {(this.label ||
          (!this.hideValidationMessage &&
            (this.touched || this.alwaysShowValidationMessage) &&
            this.validationMessage)) && (
          <omk-label
            for={this.radioId}
            style={{ order: this.labelBefore ? '-1' : '1' }}
            sublabel={this.sublabel}
            validationMessage={this.validationMessage}
            disabled={this.disabled}
            hideValidationMessage={
              !(this.touched && (this.required || this.customValidationMessage?.length > 0)) ||
              this.hideValidationMessage
            }
          >
            {this.label}
          </omk-label>
        )}
        <input
          class="hide"
          aria-label={this.ariaLabel}
          name={this.groupName}
          type="radio"
          checked={this.checked}
          value={this.value}
          required={this.required}
          id={this.radioId}
          onInput={this.inputCompletedHandler.bind(this)}
          onBlur={this.blurCompletedHandler.bind(this)}
          disabled={this.disabled}
          ref={el => {
            this.innerInputElement = el;
          }}
        />
        <omk-radio-button
          hasError={this.touched && this.isGroupInvalid}
          hasFocus={this.hasFocus}
          hasHover={this.hasHover}
          checked={this.checked}
          disabled={this.disabled}
        ></omk-radio-button>
      </Host>
    );
  }

  /** @internal Run validation for the radio with a boolean parameter to set it as touched.
   * If `setTouched` is not provided or set to false, the touched state will not be changed.
   * Validation messages are not shown until the element is touched.
   * The only built-in validation for radio options is "required".
   * Custom validation messages can be set through the customValidationMessage property, and don't require this method to be called afterwards.
   * Returns the validity state as a boolean. */
  @Method()
  async validate(setTouched?: boolean) {
    if (setTouched) {
      this.touched = true;
    }
    if (this.disabled) return true;

    if (this.customValidationMessage) {
      this.internals?.setValidity({ customError: true }, this.customValidationMessage, this.innerInputElement);
    } else if (this.innerInputElement) {
      this.internals?.setValidity(
        this.innerInputElement.validity,
        getOverriddenValidationMessage(
          libraryComponentOptions.componentValidationMessageOverrides?.['omk-radio'],
          this.innerInputElement.validity,
          this.innerInputElement.validationMessage,
        ),
        this.innerInputElement,
      );
    }

    this.validity = this.internals?.validity;
    this.validationMessage = this.internals?.validationMessage;

    return this.validity?.valid;
  }

  async componentDidRender() {
    // When we implement a radio group component, we shouldn't have to do this anymore for the cases where it's used; group validity can be passed down through the prop directly.
    // We must validate the group after render because we need the inner Material element to update validity state first.
    await this.validateGroup(this.touched);
  }

  /** @internal */
  async validateGroup(setTouched: boolean) {
    if (this.groupName) {
      const siblings = this.internals.form
        ? (Array.from(
            Array.from(this.internals.form.elements).filter(
              e => (e as HTMLOmkRadioElement).groupName === this.groupName,
            ),
          ) as HTMLOmkRadioElement[])
        : (Array.from(
            document.querySelectorAll(
              `${CSS.escape(this.host.tagName.toLowerCase())}[group-name=${CSS.escape(this.groupName)}]`,
            ),
          ) as HTMLOmkRadioElement[]);
      const groupValidity = await Promise.all(
        siblings.map(radio => (radio as HTMLOmkRadioElement)?.validate(setTouched)),
      );

      const isGroupInvalid = groupValidity.some(v => v === false);
      // Setting a boolean prop to the same value doesn't cause a re-render, but we might as well skip setting it if we know anyway.
      if (this.isGroupInvalid !== isGroupInvalid) {
        // Setting this triggers another render cycle. We might be able to optimize this to only run after the last radio in the group has rendered.
        siblings.forEach(s => (s.isGroupInvalid = isGroupInvalid));
      }
    } else {
      this.isGroupInvalid = !(await this.validate(setTouched));
    }
  }

  formAssociatedCallback(form) {
    this.internals.role = 'radio';
    selectFormSubmitters(form).forEach(submitter => {
      submitter.addEventListener('click', () => {
        this.validate(true);
      });
      this.hasRegisteredFormCallback = true;
    });
  }
}
